import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import "./LoginScreen.css";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import config from "../../config";
import ErrorPopup from "../../components/common/ErrorPopup";

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("signin");
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const initialValues = {
    emailId: "",
    password: "",
  };

  const validationSchema = Yup.object({
    emailId: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid email address."
      )
      .required("Email ID is required."),
    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/,
        "Password must meet complexity requirements."
      ),
  });

  const handleSubmit = async (values) => {
    console.log("Form values:", values);
    setErrorMessage("");
    setShowErrorPopup(false);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.SIGNIN_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
          redirect: "manual",
          credentials: "include",
        }
      );

      console.log("Raw response:", response);

      if (response.status === 302) {
        const location = response.headers.get("Location");
        console.warn("Redirected to:", location);
        setErrorMessage(
          "Unexpected redirect (302). Backend might be expecting form login."
        );
        setShowErrorPopup(true);
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        console.error("Error response:", data);
        setErrorMessage(data.message || "Login failed. Please try again.");
        setShowErrorPopup(true);
        return;
      }

      console.log("Success response:", data);
      localStorage.setItem("Authorization", data.token);
      localStorage.setItem("userEmail", values.emailId);
      localStorage.setItem("firstName", data.firstName);
      localStorage.setItem("lastName", data.lastName);
      localStorage.setItem("role", data.role);
      localStorage.setItem("companyName", data.orgName);
      localStorage.setItem("orgId", data.orgId);

      navigate("/dashboard");
    } catch (error) {
      console.error("Network error:", error);
      setErrorMessage("Something went wrong. Please try again.");
      setShowErrorPopup(true);
    }
  };

  const handleClick = (tab) => {
    setActiveTab(tab);
    navigate(`/${tab}`);
  };

  const handleForgotPasswordClick = () => {
    navigate("/forget-password");
  };

  return (
    <div className='login-container'>
      <div className='login-box'>
        {errorMessage && showErrorPopup && (
          <ErrorPopup
            message={errorMessage}
            onClose={() => {
              setShowErrorPopup(false);
              setErrorMessage("");
            }}
          />
        )}
        <div className='tab-switch'>
          <button
            className={`tab ${activeTab === "signup" ? "active" : ""}`}
            onClick={() => handleClick("signup")}
          >
            Sign up
          </button>
          <button
            className={`tab ${activeTab === "signin" ? "active" : ""}`}
            onClick={() => handleClick("signin")}
          >
            Sign in
          </button>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className='login-form'>
              <div className='input-group'>
                <label>
                  Email ID<span className='required-asterisk'>*</span>
                </label>
                <Field
                  type='email'
                  placeholder='Enter your email'
                  name='emailId'
                />
                <ErrorMessage
                  name='emailId'
                  component='div'
                  className='error'
                />
              </div>

              <div className='input-group password-group'>
                <label>
                  Password<span className='required-asterisk'>*</span>
                </label>
                <div className='password-wrapper'>
                  <Field
                    type={showPassword ? "text" : "password"}
                    placeholder='Enter your password'
                    name='password'
                  />
                  <span
                    className='toggle-password'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
                  </span>
                </div>
                <ErrorMessage
                  name='password'
                  component='div'
                  className='error'
                />
              </div>

              <div className='forgot-password'>
                <span
                  onClick={handleForgotPasswordClick}
                  className='forgot-password-link'
                >
                  Forget Password
                </span>
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='submit-button'
              >
                {isSubmitting ? "Submitting..." : "Sign In"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default SignIn;
